// frontend/src/i18n/landing.js
// Standalone i18n dictionary for the marketing Landing page (uk / en / ru).
// The rest of the app uses i18n/translations.js via useLanguage().t();
// the Landing keeps its copy here to avoid surgically editing the large
// shared translations file. Access pattern in Landing.js:
//   const { lang } = useLanguage();
//   const T = LANDING[lang] || LANDING.en;
//   ... {T.heroTitle1} ...
//
// Prices are kept out of this file on purpose — they live as literals in the
// pricing block so there is one obvious place to change them.
// Individual plan benefits are NOT here: the pricing block reuses the
// Subscription page keys (subBenefit1..7) from i18n/translations.js.

export const LANDING = {
  uk: {
    // Navbar
    navSignin: "Увійти",
    navTry: "Спробувати 7 днів",

    // Hero
    heroBadge: "AI Food OS для сімей · 7 днів безкоштовно",
    heroTitle1: "Розумна кухня",
    heroTitleAccent: "для всієї родини.",
    heroSub1: "Ваш холодильник уже знає, що на вечерю.",
    heroSub2: "Тепер знаєте і ви.",
    heroCta: "Спробувати MealPill — 7 днів безкоштовно",
    heroNote: "7 днів безкоштовно · скасування будь-коли",

    // Problem stats
    realityLabel: "Реальність",
    realityTitle: "Створено для хаосу сучасного сімейного життя",
    stat1Num: "$1 500+",
    stat1Text: "Стільки середня сім'я щороку викидає на харчові відходи",
    stat2Num: "4 години",
    stat2Text: "Щотижня на планування їжі, покупки й рішення, що на вечерю",
    stat3Num: "34%",
    stat3Text: "Сімейних прийомів їжі стають доставкою. Не за вибором — від утоми.",

    // How it works
    howLabel: "Як це працює",
    howTitle: "Один AI-мозок. Три турботи з ваших плечей.",
    step1Title: "Скажіть, що у вашому холодильнику",
    step1Text: "Додайте інгредієнти за секунди. Ми стежимо за термінами, кількістю й тим, що закінчується — автоматично.",
    step2Title: "AI обирає один ідеальний план",
    step2Text: "Не 50 варіантів рецептів. Один продуманий тижневий план на основі вашого холодильника, сім'ї та ритму.",
    step3Title: "Готуйте. Купуйте. Готово.",
    step3Text: "Ми самі складаємо список покупок — лише те, чого бракує. Інструкції готові, коли готові ви.",
    demoLabel: "СПРОБУЙТЕ ЗАРАЗ",
    demoTitle: "Не вірте на слово — подивіться",
    demoText: "Додайте п'ять продуктів, які є вдома, і MealPill складе з них день: сніданок, обід і вечерю з рецептами, калоріями й канадськими цінами. Без акаунта і без картки.",
    demoCta: "Подивитись демо",
    demoNote: "Одна справжня генерація на день",
    demoB: "Сніданок",
    demoL: "Обід",
    demoD: "Вечеря",
    demoTotal: "Разом за день",
    demoDish1: "Яєчня",
    demoDish2: "Курка з рисом",
    demoDish3: "Теплий салат",
    demoKcal: "ккал",

    // Hero mockup cards. "Smart fridge" is just "Холодильник" in uk/ru: the
    // card is 240px wide and shares its header row with the item-count badge,
    // which the longer wording pushes onto a second line.
    heroMockPlan: "План на сьогодні",
    heroMockDish1: "Тост з авокадо",
    heroMockDish2: "Паста з грибами",
    heroMockDish3: "Лосось з броколі",
    heroMockFridge: "Холодильник",
    heroMockItems: "12 продуктів",
    heroMockList: "Просто купи це",
    heroMockBuy1: "Олія оливкова · 1 пляшка",
    heroMockBuy2: "Хліб цільнозерновий",
    heroMockBuy3: "Лосось · 200 г",
    heroMockBadge: "Менше відходів",

    // Features
    featLabel: "Повний мозок",
    featTitle: "Усе про їжу. Нічого зайвого.",
    feat1Title: "Розумний холодильник",
    feat1Text: "Стежте за інгредієнтами й термінами, отримуйте сповіщення, поки їжа не зіпсувалась.",
    feat2Title: "AI-мозок меню",
    feat2Text: "Персональні тижневі плани, що підлаштовуються під ваш холодильник, смак і розклад.",
    feat3Title: "Авто-покупки",
    feat3Text: "Списки покупок, що знають, що у вас уже є. Купуйте лише те, чого бракує.",
    feat4Title: "Сім'я передусім",
    feat4Text: "Незабаром: спільні запаси, плани й списки для всієї родини.",
    feat5Title: "Багатомовність",
    feat5Text: "Англійська, українська, російська — рецепти з перекладом і збереженими вподобаннями.",
    feat6Title: "Розумна аналітика",
    feat6Text: "Стежте за відходами, різноманіттям, балансом і заощадженнями. Готуйте краще щотижня.",

    // Pricing
    priceLabel: "Ціни",
    priceTitle: "Прості плани для кожної кухні",
    priceSub: "7-денний пробний період. Скасуйте будь-коли.",
    planIndividual: "Індивідуальна",
    planIndividualDesc: "Для одного кухаря",
    planFamily: "Сімейна",
    planFamilyDesc: "Для всієї родини",
    billMonthly: "Щомісяця",
    billAnnual: "Щороку",
    perMo: "/міс",
    perYr: "/рік",
    annualSave: "2 місяці в подарунок",
    comingSoon: "Незабаром",
    individualCta: "Почати пробний період",
    familyNotifyCta: "Сповістити про запуск",
    familyF1: "Усе з Індивідуальної",
    familyF2: "Спільний холодильник, плани й списки",
    familyF3: "До 6 членів родини",
    familyF4: "Один рахунок на всю родину",

    // Notify-me modal (Family waitlist)
    notifyTitle: "Дізнайтесь про запуск Family",
    notifySub: "Ми надішлемо рівно один лист, коли план Family запуститься — і більше нічого.",
    notifyPlaceholder: "your@email.com",
    notifySubmit: "Сповістити мене",
    notifySuccess: "Ви у списку! Ми напишемо на запуску.",
    notifyAlready: "Ви вже у списку.",
    notifyError: "Щось пішло не так. Спробуйте ще раз.",
    notifyInvalid: "Введіть коректний email.",
    notifyPromise: "Жодного спаму. Один лист, один раз.",

    // Final CTA
    finalTitle: "Готування не має бути ще однією роботою на повний день.",
    finalSub: "Почніть із 7 днів безкоштовно. Ми беремо планування — ви берете задоволення від їжі.",
    finalCta: "Спробувати MealPill — 7 днів безкоштовно",

    // Footer
    footerTagline: "AI Food OS для сімей. Плануйте їжу, менше відходів, більше часу.",
    footerLegal: "Правове",
    footerPrivacy: "Приватність",
    footerTerms: "Умови",
    footerContact: "Контакти",
    footerLocation: "Ванкувер, Британська Колумбія · Канада",
    footerCopyright: "© 2026 MealPill. Зроблено з любов'ю у Ванкувері.",
    footerSolo: "Створено засновником-одинаком, якому набридло питання «що на вечерю?»",
  },

  en: {
    // Navbar
    navSignin: "Sign in",
    navTry: "Start free trial",

    // Hero
    heroBadge: "AI Food OS for Families · 7 days free",
    heroTitle1: "Take a chill pill from",
    heroTitleAccent: "meal planning.",
    heroSub1: "Your fridge already knows what's for dinner.",
    heroSub2: "Now you do too.",
    heroCta: "Try MealPill — 7 days free",
    heroNote: "7 days free · cancel anytime",

    // Problem stats
    realityLabel: "The reality",
    realityTitle: "Built for the chaos of modern family life",
    stat1Num: "$1,500+",
    stat1Text: "Average family throws away in food waste each year",
    stat2Num: "4 hours",
    stat2Text: "Spent weekly planning meals, shopping, and deciding what's for dinner",
    stat3Num: "34%",
    stat3Text: "Of family meals end up as takeout. Not by choice — by exhaustion.",

    // How it works
    howLabel: "How it works",
    howTitle: "One AI brain. Three things off your plate.",
    step1Title: "Tell us what's in your fridge",
    step1Text: "Add ingredients in seconds. We track expiry, quantities, and what's running low — automatically.",
    step2Title: "AI picks one perfect plan",
    step2Text: "Not 50 recipe suggestions. One thoughtful weekly plan based on your fridge, family, and rhythm.",
    step3Title: "Cook. Shop. Done.",
    step3Text: "We auto-build your grocery list — only what's missing. Cooking instructions ready when you are.",
    demoLabel: "TRY IT NOW",
    demoTitle: "Do not take our word for it",
    demoText: "Add five things you have at home and MealPill builds a day from them — breakfast, lunch and dinner with recipes, calories and Canadian prices. No account, no card.",
    demoCta: "See the demo",
    demoNote: "One real generation a day",
    demoB: "Breakfast",
    demoL: "Lunch",
    demoD: "Dinner",
    demoTotal: "Day total",
    demoDish1: "Scrambled eggs",
    demoDish2: "Chicken with rice",
    demoDish3: "Warm salad",
    demoKcal: "kcal",

    // Hero mockup cards.
    heroMockPlan: "Today's plan",
    heroMockDish1: "Avocado toast",
    heroMockDish2: "Mushroom pasta",
    heroMockDish3: "Salmon, broccoli",
    heroMockFridge: "Smart fridge",
    heroMockItems: "12 items",
    heroMockList: "Just buy this",
    heroMockBuy1: "Olive oil · 1 bottle",
    heroMockBuy2: "Whole grain bread",
    heroMockBuy3: "Salmon · 200g",
    heroMockBadge: "Less waste",

    // Features
    featLabel: "The full brain",
    featTitle: "Everything food. Nothing extra.",
    feat1Title: "Smart Fridge",
    feat1Text: "Track ingredients, watch expiry dates, get alerts before food goes bad.",
    feat2Title: "AI Meal Brain",
    feat2Text: "Personalized weekly plans that adapt to your fridge, taste, and schedule.",
    feat3Title: "Auto Grocery",
    feat3Text: "Shopping lists that know what you already have. Buy only what's missing.",
    feat4Title: "Family-First",
    feat4Text: "Coming soon: shared inventory, plans, and lists across the whole household.",
    feat5Title: "Multilingual",
    feat5Text: "English, Ukrainian, Russian — recipes translated with your preferences intact.",
    feat6Title: "Smart Insights",
    feat6Text: "Track waste, variety, balance, and savings over time. Cook better, week by week.",

    // Pricing
    priceLabel: "Pricing",
    priceTitle: "Simple plans for every kitchen",
    priceSub: "7-day free trial. Cancel anytime.",
    planIndividual: "Individual",
    planIndividualDesc: "For one home cook",
    planFamily: "Family",
    planFamilyDesc: "For the whole household",
    billMonthly: "Monthly",
    billAnnual: "Annual",
    perMo: "/mo",
    perYr: "/yr",
    annualSave: "2 months free",
    comingSoon: "Coming soon",
    individualCta: "Start free trial",
    familyNotifyCta: "Notify me at launch",
    familyF1: "Everything in Individual",
    familyF2: "Shared fridge, plans & lists",
    familyF3: "Up to 6 family members",
    familyF4: "One bill for the whole family",

    // Notify-me modal (Family waitlist)
    notifyTitle: "Get notified when Family launches",
    notifySub: "We'll send you exactly one email when the Family plan goes live — nothing else.",
    notifyPlaceholder: "your@email.com",
    notifySubmit: "Notify me",
    notifySuccess: "You're on the list! We'll email you at launch.",
    notifyAlready: "You're already on the list.",
    notifyError: "Something went wrong. Please try again.",
    notifyInvalid: "Please enter a valid email.",
    notifyPromise: "No spam, ever. One email, once.",

    // Final CTA
    finalTitle: "Cooking shouldn't be another full-time job.",
    finalSub: "Start with a 7-day free trial. We'll handle the planning so you can handle the eating.",
    finalCta: "Try MealPill — 7 days free",

    // Footer
    footerTagline: "AI Food OS for Families. Plan meals, reduce waste, save time.",
    footerLegal: "Legal",
    footerPrivacy: "Privacy",
    footerTerms: "Terms",
    footerContact: "Contact",
    footerLocation: "Vancouver, BC · Canada",
    footerCopyright: "© 2026 MealPill. Built with love in Vancouver.",
    footerSolo: "Made by a solo founder who got tired of what's for dinner.",
  },

  ru: {
    // Navbar
    navSignin: "Войти",
    navTry: "Попробовать 7 дней",

    // Hero
    heroBadge: "AI Food OS для семей · 7 дней бесплатно",
    heroTitle1: "Умная кухня",
    heroTitleAccent: "для всей семьи.",
    heroSub1: "Ваш холодильник уже знает, что на ужин.",
    heroSub2: "Теперь знаете и вы.",
    heroCta: "Попробовать MealPill — 7 дней бесплатно",
    heroNote: "7 дней бесплатно · отмена в любой момент",

    // Problem stats
    realityLabel: "Реальность",
    realityTitle: "Создано для хаоса современной семейной жизни",
    stat1Num: "$1 500+",
    stat1Text: "Столько средняя семья ежегодно выбрасывает на пищевые отходы",
    stat2Num: "4 часа",
    stat2Text: "Каждую неделю на планирование еды, покупки и решение, что на ужин",
    stat3Num: "34%",
    stat3Text: "Семейных приёмов пищи становятся доставкой. Не по выбору — от усталости.",

    // How it works
    howLabel: "Как это работает",
    howTitle: "Один AI-мозг. Три заботы с ваших плеч.",
    step1Title: "Скажите, что в вашем холодильнике",
    step1Text: "Добавьте ингредиенты за секунды. Мы следим за сроками, количеством и тем, что заканчивается — автоматически.",
    step2Title: "AI выбирает один идеальный план",
    step2Text: "Не 50 вариантов рецептов. Один продуманный недельный план на основе вашего холодильника, семьи и ритма.",
    step3Title: "Готовьте. Покупайте. Готово.",
    step3Text: "Мы сами составляем список покупок — только то, чего не хватает. Инструкции готовы, когда готовы вы.",
    demoLabel: "ПОПРОБУЙТЕ СЕЙЧАС",
    demoTitle: "Не верьте на слово — посмотрите",
    demoText: "Добавьте пять продуктов, которые есть дома, и MealPill соберёт из них день: завтрак, обед и ужин с рецептами, калориями и канадскими ценами. Без аккаунта и без карты.",
    demoCta: "Посмотреть демо",
    demoNote: "Одна настоящая генерация в день",
    demoB: "Завтрак",
    demoL: "Обед",
    demoD: "Ужин",
    demoTotal: "Всего за день",
    demoDish1: "Яичница",
    demoDish2: "Курица с рисом",
    demoDish3: "Тёплый салат",
    demoKcal: "ккал",

    // Hero mockup cards.
    heroMockPlan: "План на сегодня",
    heroMockDish1: "Тост с авокадо",
    heroMockDish2: "Паста с грибами",
    heroMockDish3: "Лосось с брокколи",
    heroMockFridge: "Холодильник",
    heroMockItems: "12 продуктов",
    heroMockList: "Просто купи это",
    heroMockBuy1: "Масло оливковое · 1 бутылка",
    heroMockBuy2: "Хлеб цельнозерновой",
    heroMockBuy3: "Лосось · 200 г",
    heroMockBadge: "Меньше отходов",

    // Features
    featLabel: "Полный мозг",
    featTitle: "Всё о еде. Ничего лишнего.",
    feat1Title: "Умный холодильник",
    feat1Text: "Следите за ингредиентами и сроками, получайте уведомления, пока еда не испортилась.",
    feat2Title: "AI-мозг меню",
    feat2Text: "Персональные недельные планы, подстраивающиеся под ваш холодильник, вкус и расписание.",
    feat3Title: "Авто-покупки",
    feat3Text: "Списки покупок, которые знают, что у вас уже есть. Покупайте только то, чего не хватает.",
    feat4Title: "Семья прежде всего",
    feat4Text: "Скоро: общие запасы, планы и списки для всей семьи.",
    feat5Title: "Многоязычность",
    feat5Text: "Английский, украинский, русский — рецепты с переводом и сохранёнными предпочтениями.",
    feat6Title: "Умная аналитика",
    feat6Text: "Следите за отходами, разнообразием, балансом и экономией. Готовьте лучше каждую неделю.",

    // Pricing
    priceLabel: "Цены",
    priceTitle: "Простые планы для каждой кухни",
    priceSub: "7-дневный пробный период. Отмените в любой момент.",
    planIndividual: "Индивидуальная",
    planIndividualDesc: "Для одного повара",
    planFamily: "Семейная",
    planFamilyDesc: "Для всей семьи",
    billMonthly: "Ежемесячно",
    billAnnual: "Ежегодно",
    perMo: "/мес",
    perYr: "/год",
    annualSave: "2 месяца в подарок",
    comingSoon: "Скоро",
    individualCta: "Начать пробный период",
    familyNotifyCta: "Уведомить о запуске",
    familyF1: "Всё из Индивидуальной",
    familyF2: "Общий холодильник, планы и списки",
    familyF3: "До 6 членов семьи",
    familyF4: "Один счёт на всю семью",

    // Notify-me modal (Family waitlist)
    notifyTitle: "Узнайте о запуске Family",
    notifySub: "Мы отправим ровно одно письмо, когда план Family запустится — и больше ничего.",
    notifyPlaceholder: "your@email.com",
    notifySubmit: "Уведомить меня",
    notifySuccess: "Вы в списке! Мы напишем на запуске.",
    notifyAlready: "Вы уже в списке.",
    notifyError: "Что-то пошло не так. Попробуйте ещё раз.",
    notifyInvalid: "Введите корректный email.",
    notifyPromise: "Никакого спама. Одно письмо, один раз.",

    // Final CTA
    finalTitle: "Готовка не должна быть ещё одной работой на полный день.",
    finalSub: "Начните с 7 дней бесплатно. Мы берём планирование — вы берёте удовольствие от еды.",
    finalCta: "Попробовать MealPill — 7 дней бесплатно",

    // Footer
    footerTagline: "AI Food OS для семей. Планируйте еду, меньше отходов, больше времени.",
    footerLegal: "Правовое",
    footerPrivacy: "Конфиденциальность",
    footerTerms: "Условия",
    footerContact: "Контакты",
    footerLocation: "Ванкувер, Британская Колумбия · Канада",
    footerCopyright: "© 2026 MealPill. Сделано с любовью в Ванкувере.",
    footerSolo: "Создано основателем-одиночкой, которому надоел вопрос «что на ужин?»",
  },
};

export default LANDING;
